import { Autocomplete, debounce, ListItem, ListItemText } from "@mui/material";
import { SxProps } from "@mui/system";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import CommonTextField from "../../commonTextField/CommonTextField";
import useCustomerAutocomplete, {
  CustomerOption,
} from "./useCustomerAutocomplete";
import { useTranslation } from "react-i18next";

type Props = {
  name: string;
  fullWidth?: boolean;
  isLoading?: boolean;
  inputSx?: SxProps;
  includeCompanies?: boolean;
  required?: boolean;
  rules?: RegisterOptions;
  /**
   * Optional override for onChange.
   * Use this to perform side effects like resetting dependent fields
   * when customerOrganization changes, instead of using a useEffect.
   */
  onChangeOverride?: (val: CustomerOption | null) => void;
  error?: boolean;
  disabled?: boolean;
};

const CustomerAutocompleteField = ({
  name,
  fullWidth,
  isLoading = false,
  inputSx = {},
  includeCompanies = false,
  required = false,
  rules,
  onChangeOverride,
  error = false,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const { customerOptions, setCustomerSearchTerm } = useCustomerAutocomplete({
    includeCompanies,
  });

  const { control } = useFormContext();

  const onInputChange = debounce((value: string, reason: string) => {
    if (reason === "input") {
      setCustomerSearchTerm(value);
    }
  }, 400);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...(required && {
          required: { value: true, message: "required" },
        }),
        ...rules,
      }}
      render={({ field: { value, onChange } }) => (
        <Autocomplete<CustomerOption, false, undefined, undefined>
          id="customer-autocomplete"
          fullWidth={fullWidth}
          multiple={false}
          disabled={disabled}
          sx={{
            // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
            "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
              { minWidth: "30px" },
          }}
          value={value || null}
          loading={isLoading}
          options={customerOptions}
          getOptionLabel={(option) => option.name}
          {...(includeCompanies && {
            groupBy: (option) => option.companyName || "",
          })}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              <ListItemText>{option.name}</ListItemText>
            </ListItem>
          )}
          {...(includeCompanies && {
            renderGroup: ({ group, children }) => (
              <AutocompleteGroupedList
                key={group}
                group={group}
                children={children}
              />
            ),
          })}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => {
            if (onChangeOverride) onChangeOverride(newValue);
            else onChange(newValue);
          }}
          onInputChange={(_, value, reason) => onInputChange(value, reason)}
          renderInput={(params) => (
            <CommonTextField {...params} sx={inputSx} error={error} />
          )}
          noOptionsText={t("common:writeToSearch")}
        />
      )}
    />
  );
};

export default CustomerAutocompleteField;
